// ---------------------------------------------------------------------------
// AppErrorBoundary — the last line before a blank screen (AL-2004)
// ---------------------------------------------------------------------------
// The outermost component in main.tsx, so it also catches what fails above the
// router: the providers, the theme, the public pages. It needs nothing from
// them — react-i18next falls back to the global i18n instance without the
// provider.
// ---------------------------------------------------------------------------

import { useState, type ReactNode } from 'react';
import { Sentry } from '@/config/sentry';
import { ErrorFallback } from './ErrorFallback';
import { useReloadPending } from './useReloadPending';
import { isStaleBuildError } from './isStaleBuildError';

// Module-level for the same reason as RouteFallback: Sentry renders the
// fallback with createElement, so an inline arrow is a new type every render.
function AppFallback({ error }: { readonly error: unknown }) {
  const [staleBuild] = useState(() => isStaleBuildError(error));
  const [reloading] = useReloadPending();
  if (reloading) return <></>;
  return <ErrorFallback variant="app" staleBuild={staleBuild} />;
}

export function AppErrorBoundary({ children }: { readonly children: ReactNode }) {
  return (
    <Sentry.ErrorBoundary
      beforeCapture={scope => scope.setTag('error_boundary', 'app')}
      fallback={AppFallback}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
